/**
 * Reserved system comment persisting table column widths across the markdown
 * round-trip: `<!-- colwidths:180,320 -->` on its own line immediately before
 * a pipe table. GFM has no column-width syntax, so the serializer emits this
 * marker and the parser stamps the widths back onto the table's first-row
 * cells as `colwidth` attributes — the attribute TipTap's table extension
 * parses natively, which is all the resize plugin needs to restore layout.
 *
 * Like the section markers in `section-markers.ts`, the marker is consumed on
 * parse and must never render as user-visible text.
 */

/**
 * Two deliberately different grammars share the `colwidths:` namespace:
 *
 * - **Stamping is strict** (`TABLE_COLWIDTHS_MARKER_REGEX`): only a
 *   well-formed width list may reach the editor as layout, and widths are
 *   capped at four digits so a malformed marker can't stamp absurd pixel
 *   values.
 * - **Hiding is generous** (`TABLE_COLWIDTHS_NAMESPACE_SOURCE`): any comment
 *   in the reserved namespace — including an LLM-mangled one — is stripped or
 *   stashed rather than shown. System-namespace garbage must never render on
 *   a clinical surface, even when it's too malformed to stamp.
 */

/** Matches a whole marker line; group 1 is the comma-separated width list. */
export const TABLE_COLWIDTHS_MARKER_REGEX =
  /^<!--\s*colwidths:(\d{1,4}(?:,\d{1,4})*)\s*-->$/;

/**
 * Regex source for anything in the reserved namespace, however malformed.
 * Single-line by construction (`[^\n]`); embed in hiding/stripping patterns.
 */
export const TABLE_COLWIDTHS_NAMESPACE_SOURCE = 'colwidths:[^\\n]*?';

/**
 * Parses a marker line into per-column pixel widths, index-aligned with the
 * table's columns. Returns null when the line is not a marker.
 */
export const parseTableColwidthsMarker = (line: string): number[] | null => {
  const match = TABLE_COLWIDTHS_MARKER_REGEX.exec(line.trim());
  if (!match) return null;
  return (match[1] ?? '').split(',').map((width) => parseInt(width, 10));
};

/**
 * Drops marker lines from raw markdown, collapsing the line they occupied.
 * For presentation/EHR destinations that consume stored markdown without
 * running it through the markdown→HTML parse (which consumes markers itself).
 */
const NAMESPACE_LINE_REGEX = new RegExp(
  `^<!--\\s*${TABLE_COLWIDTHS_NAMESPACE_SOURCE}-->$`
);

/**
 * True for any whole line in the reserved namespace, valid or malformed.
 * Parsers use this to consume (and drop) a namespace line that failed the
 * strict grammar, so it never renders.
 */
export const isTableColwidthsNamespaceLine = (line: string): boolean =>
  NAMESPACE_LINE_REGEX.test(line.trim());

const MID_DOCUMENT_MARKER_LINE_REGEX = new RegExp(
  `\\n[^\\S\\n]*<!--\\s*${TABLE_COLWIDTHS_NAMESPACE_SOURCE}-->[^\\S\\n]*(?=\\n|$)`,
  'g'
);
const LEADING_MARKER_LINE_REGEX = new RegExp(
  `^[^\\S\\n]*<!--\\s*${TABLE_COLWIDTHS_NAMESPACE_SOURCE}-->[^\\S\\n]*\\n?`
);

export const stripTableColwidthsMarkerLines = (markdown: string): string =>
  markdown
    // Marker on its own line mid-document: remove the line and its preceding
    // newline so no blank line is left behind.
    .replace(MID_DOCUMENT_MARKER_LINE_REGEX, '')
    // Marker on the first line of the document.
    .replace(LEADING_MARKER_LINE_REGEX, '');
